import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, QrCode, Trash2, ExternalLink } from "lucide-react";
import { api, errorText, fileUrl } from "@/lib/api";
import { PageTitle } from "@/admin/AdminLayout";

export async function downloadQrZip(ex) {
  const res = await api.get(`/admin/exhibitions/${ex.id}/qr.zip`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a"); a.href = url; a.download = `qr-${ex.slug}.zip`; a.click();
  URL.revokeObjectURL(url);
}

export default function ExhibitionsAdmin() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [settings, setSettings] = useState(null);
  const refresh = () => api.get("/admin/exhibitions").then((r) => setList(r.data));
  useEffect(() => { refresh(); api.get("/admin/settings").then((r) => setSettings(r.data)); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/exhibitions", { name }); setName(""); toast.success("Exhibition created"); refresh(); } catch (err) { toast.error(errorText(err)); }
  };
  const remove = async (ex) => {
    if (!window.confirm(`Delete exhibition "${ex.name}"? Its QR codes will stop working.`)) return;
    try { await api.delete(`/admin/exhibitions/${ex.id}`); refresh(); } catch (err) { toast.error(errorText(err)); }
  };

  return (
    <div data-testid="admin-exhibitions-page">
      <PageTitle>Exhibitions</PageTitle>
      {settings && (
        <div className="border border-[var(--line)] p-4 mb-6 text-xs text-[var(--muted)]" data-testid="qr-base-url-note">
          QR codes and links use the base URL <span className="text-white font-mono">{settings.public_base_url}</span> — pattern <span className="font-mono">{settings.qr_url_pattern}</span>. Configured via <span className="font-mono">PUBLIC_BASE_URL</span> (production: https://banksyguide.com).
        </div>
      )}
      <form onSubmit={add} className="flex gap-3 border border-[var(--line)] p-4 mb-8" data-testid="exhibition-add-form">
        <input className="field" placeholder="New exhibition name (e.g. Berlin)" required value={name} onChange={(e) => setName(e.target.value)} data-testid="exhibition-add-name" />
        <button type="submit" className="btn-accent px-5 py-3 text-xs flex items-center gap-2 whitespace-nowrap" data-testid="exhibition-add-submit"><Plus size={14} /> Create</button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {list.map((ex) => (
          <div key={ex.id} className="border border-[var(--line)] p-5 flex flex-col gap-4" data-testid={`exhibition-card-${ex.slug}`} style={{ borderTopColor: ex.accent_color, borderTopWidth: 3 }}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex-none overflow-hidden">{ex.logo_url ? <img src={fileUrl(ex.logo_url)} alt="" className="w-full h-full object-contain" /> : <div className="art-placeholder w-full h-full text-[0.5rem]">logo</div>}</div>
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-2xl uppercase flex items-center gap-2">{ex.name} <span className={`tag ${ex.type === "permanent" ? "tag-permanent" : "tag-touring"}`} data-testid={`exhibition-type-${ex.slug}`}>{ex.type || "touring"}</span></h2>
                <a href={ex.public_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--muted)] hover:text-white inline-flex items-center gap-1 truncate" data-testid={`exhibition-public-link-${ex.slug}`}>/e/{ex.slug} <ExternalLink size={11} /></a>
              </div>
              <span className="text-xs text-[var(--muted)]">{ex.artwork_ids.length} artworks</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to={`/admin/exhibitions/${ex.id}`} className="btn-accent px-4 py-2 text-xs" data-testid={`exhibition-edit-${ex.slug}`}>Edit</Link>
              <button type="button" onClick={() => downloadQrZip(ex).catch((e) => toast.error(errorText(e)))} className="border border-[var(--line)] px-4 py-2 text-xs flex items-center gap-2 hover:border-white transition-colors" data-testid={`exhibition-qr-download-${ex.slug}`}><QrCode size={14} /> Download QR codes (PNG + SVG)</button>
              <button type="button" onClick={() => remove(ex)} className="ctrl !w-9 !h-9 text-[var(--muted)] hover:text-accent ml-auto" aria-label="Delete" data-testid={`exhibition-delete-${ex.slug}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
